import * as React from 'react';
import {
  Button,
  BUTTON_GROUP_CHILD,
  ButtonGroup,
  cn,
  Dropdown,
  Label,
  Popover,
  Toolbar,
} from '@heroui/react';
import type { IconProps } from '@tabler/icons-react';
import {
  IconDots,
  IconEar,
  IconLanguage,
  IconPalette,
  IconShare,
} from '@tabler/icons-react';
import type { KnowledgeBaseMetaBackground } from './knowledge-base-meta';
import { isMetaBackgroundSolidColor } from './knowledge-base-meta';

type IconComp = React.ComponentType<IconProps>;

/** 嵌在 Popover / Dropdown 内时须显式传入，否则 `Button` 不是 `ButtonGroup` 下 `Group` 的直接子节点，无法连成一体。 */
const inButtonGroup = { [BUTTON_GROUP_CHILD]: true as const };

/** 纯色选项：与 `meta.background` 在 `mode: "color"` 下的 `color` 字段对应（hex） */
export type SolidBackgroundSwatch = {
  tw: string;
  label: string;
  name: string;
  /** 写入 `meta.background.color` */
  hex: string;
};

/**
 * 纯色背景：按既定顺序平铺。Tailwind 默认色 hex 与官方默认主题一致；
 * Taupe/Mauve/Mist/Olive 为设计补充近似色。
 */
export const SOLID_BACKGROUND_SWATCHES: readonly SolidBackgroundSwatch[] = [
  { tw: 'red-500', label: '红色', name: 'Red', hex: '#ef4444' },
  { tw: 'orange-500', label: '橙色', name: 'Orange', hex: '#f97316' },
  { tw: 'amber-500', label: '琥珀色', name: 'Amber', hex: '#f59e0b' },
  { tw: 'yellow-400', label: '黄色', name: 'Yellow', hex: '#facc15' },
  { tw: 'lime-500', label: '青柠绿', name: 'Lime', hex: '#84cc16' },
  { tw: 'green-500', label: '绿色', name: 'Green', hex: '#22c55e' },
  { tw: 'emerald-500', label: '翠绿色', name: 'Emerald', hex: '#10b981' },
  { tw: 'teal-500', label: '蓝绿色', name: 'Teal', hex: '#14b8a6' },
  { tw: 'cyan-500', label: '青色', name: 'Cyan', hex: '#06b6d4' },
  { tw: 'sky-500', label: '天蓝', name: 'Sky', hex: '#0ea5e9' },
  { tw: 'blue-500', label: '蓝色', name: 'Blue', hex: '#3b82f6' },
  { tw: 'indigo-500', label: '靛蓝', name: 'Indigo', hex: '#6366f1' },
  { tw: 'violet-500', label: '紫罗兰', name: 'Violet', hex: '#8b5cf6' },
  { tw: 'purple-500', label: '紫色', name: 'Purple', hex: '#a855f7' },
  { tw: 'fuchsia-500', label: '洋红', name: 'Fuchsia', hex: '#d946ef' },
  { tw: 'pink-500', label: '粉色', name: 'Pink', hex: '#ec4899' },
  { tw: 'rose-500', label: '玫瑰', name: 'Rose', hex: '#f43f5e' },
  { tw: 'stone-500', label: '石色', name: 'Stone', hex: '#78716c' },
  { tw: 'gray-500', label: '灰色', name: 'Gray', hex: '#6b7280' },
  { tw: 'zinc-500', label: '锌灰', name: 'Zinc', hex: '#71717a' },
  { tw: 'neutral-500', label: '中性灰', name: 'Neutral', hex: '#737373' },
  { tw: 'taupe', label: '灰褐', name: 'Taupe', hex: '#9a8f82' },
  { tw: 'mauve', label: '藕灰', name: 'Mauve', hex: '#9a8796' },
  { tw: 'mist', label: '雾灰', name: 'Mist', hex: '#b0bec5' },
  { tw: 'olive', label: '橄榄', name: 'Olive', hex: '#6b7348' },
];

type ToolbarPopoverProps = {
  label: string;
  icon: IconComp;
  /** 分隔条须放在本 `Button` 内（见 ButtonGroup 文档 Anatomy）。 */
  withLeadingSeparator?: boolean;
};

function ToolbarPopover({ label, icon: Icon, withLeadingSeparator }: ToolbarPopoverProps) {
  return (
    <Popover>
      <Button
        type="button"
        isIconOnly
        size="sm"
        aria-label={label}
        className="text-foreground"
        {...inButtonGroup}
      >
        {withLeadingSeparator ? <ButtonGroup.Separator /> : null}
        <Icon className="size-4" stroke={1.25} />
      </Button>
      <Popover.Content placement="bottom end" offset={6} className="min-h-24 w-72 p-4">
        <Popover.Dialog>
          <span className="sr-only">{label}</span>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

type BackgroundSettingsPopoverProps = {
  documentBackground: KnowledgeBaseMetaBackground | null;
  onDocumentBackgroundChange: (next: KnowledgeBaseMetaBackground) => void;
  readOnly?: boolean;
  /** 分隔条须放在本 `Button` 内（见 ButtonGroup 文档 Anatomy）。 */
  withLeadingSeparator?: boolean;
};

function BackgroundSettingsPopover({
  documentBackground,
  onDocumentBackgroundChange,
  readOnly = false,
  withLeadingSeparator,
}: BackgroundSettingsPopoverProps) {
  return (
    <Popover>
      <Button
        type="button"
        isIconOnly
        size="sm"
        aria-label="背景设置"
        className="text-foreground"
        {...inButtonGroup}
      >
        {withLeadingSeparator ? <ButtonGroup.Separator /> : null}
        <IconPalette className="size-4" stroke={1.25} />
      </Button>
      <Popover.Content
        placement="bottom end"
        offset={6}
        className="max-h-[min(70vh,560px)] w-72 overflow-y-auto sm:w-80"
      >
        <Popover.Dialog>
          <Popover.Heading className="text-base font-semibold leading-none text-foreground">背景设置</Popover.Heading>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold leading-none text-foreground">纯色背景</h3>
              <div className="grid grid-cols-4 gap-x-4 gap-y-4">
                {SOLID_BACKGROUND_SWATCHES.map((item) => {
                  const selected = isMetaBackgroundSolidColor(documentBackground, item.hex);
                  const twTitle = /-\d+$/.test(item.tw)
                    ? `${item.name} (${item.tw})`
                    : `${item.name} (自定义 · ${item.tw})`;
                  return (
                    <button
                      key={item.tw}
                      type="button"
                      title={twTitle}
                      disabled={readOnly}
                      onClick={() => onDocumentBackgroundChange({ mode: 'color', color: item.hex })}
                      className="flex flex-col items-center gap-1.5 rounded-md p-1 outline-none transition-colors hover:bg-surface-secondary focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-50"
                    >
                      <span
                        className={cn(
                          'size-9 shrink-0 rounded-full border border-divider/60 shadow-sm',
                          selected && 'ring-2 ring-accent ring-offset-2 ring-offset-background',
                        )}
                        style={{ backgroundColor: item.hex }}
                      />
                      <span className="text-center text-xs text-muted-foreground">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold leading-none text-foreground">渐变背景</h3>
              <div className="flex min-h-20 items-center justify-center rounded-md border border-dashed border-divider/80 px-3 py-6 text-center text-xs text-muted-foreground">
                选项暂未开放
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold leading-none text-foreground">背景图片</h3>
              <div className="flex min-h-20 items-center justify-center rounded-md border border-dashed border-divider/80 px-3 py-6 text-center text-xs text-muted-foreground">
                选项暂未开放
              </div>
            </div>
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

export type DocToolBarProps = {
  /** 当前文档 `meta.background`（解析后） */
  documentBackground: KnowledgeBaseMetaBackground | null;
  onDocumentBackgroundChange: (next: KnowledgeBaseMetaBackground) => void;
  readOnly?: boolean;
};

/**
 * 文档编辑器中栏顶栏右侧：图标按钮（Popover / 下拉）。布局与左侧占位由 `DocPage` 顶栏承担。
 */
export function DocToolBar({
  documentBackground,
  onDocumentBackgroundChange,
  readOnly = false,
}: DocToolBarProps) {
  return (
    <Toolbar
      isAttached
      aria-label="文档工具"
      className="shrink-0 bg-transparent shadow-none"
    >
      <ButtonGroup variant="tertiary" size="sm">
        <ToolbarPopover label="音频设置" icon={IconEar} />
        <BackgroundSettingsPopover
          documentBackground={documentBackground}
          onDocumentBackgroundChange={onDocumentBackgroundChange}
          readOnly={readOnly}
          withLeadingSeparator
        />
        <ToolbarPopover label="翻译设置" icon={IconLanguage} withLeadingSeparator />
        <ToolbarPopover label="分享设置" icon={IconShare} withLeadingSeparator />
        <Dropdown>
          <Button type="button" isIconOnly size="sm" aria-label="更多" className="text-foreground" {...inButtonGroup}>
            <ButtonGroup.Separator />
            <IconDots className="size-4" stroke={1.25} />
          </Button>
          <Dropdown.Popover className="min-h-24 min-w-48 p-2" placement="bottom end" offset={6}>
            <Dropdown.Menu>
              <Dropdown.Item id="more-placeholder" textValue="更多" isDisabled>
                <Label className="text-muted-foreground">敬请期待</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </ButtonGroup>
    </Toolbar>
  );
}
